import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { ReadOnlyBanner } from '@client/components/admin/ReadOnlyBanner';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { buildCurrencyInputRules } from '@client/utils';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Drawer, Form, Input, Modal, Select, Spin, Tag, Typography } from 'antd';
import type { Decimal } from 'decimal.js';
import { Plus } from 'lucide-react';
import { DateTime } from 'luxon';
import { useState } from 'react';

const { Text } = Typography;

export const Route = createFileRoute('/admin-console/organisation-management/organisations/$orgId/wallet')({
    staticData: {
        presentation: 'overlay',
    },
    component: Wallet,
});

type Transaction = RouterOutputs['admin']['billing']['listWalletTransactions']['rows'][number];
type QueryParams = RouterInputs['admin']['billing']['listWalletTransactions'];
type OrderBy = NonNullable<QueryParams['orderBy']>;

const columnHelper = createColumnHelper<Transaction, OrderBy>();

function Wallet() {
    const { orgId } = Route.useParams();
    const [open, setOpen] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const { trpc, trpcUtils } = useTrpc();
    const { isReadOnly } = useAdminModulePermissions('orgs');
    const { data: org } = trpc.admin.org.getOrg.useQuery({ orgId });
    const [queryParams, setQueryParams] = useState<QueryParams>({
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        where: { orgId },
    });

    // Fetch wallet balance
    const { data: walletBalance, isLoading: walletLoading } = trpc.admin.billing.getWalletBalance.useQuery({ orgId });

    // Setup transactions query
    const { data: transactionsData, isLoading: transactionsLoading } =
        trpc.admin.billing.listWalletTransactions.useQuery(queryParams);

    const addTransactionMutation = trpc.admin.billing.addWalletTransaction.useMutation({
        onSuccess: () => {
            setIsModalOpen(false);
            form.resetFields();
            void trpcUtils.admin.billing.listWalletTransactions.invalidate();
            void trpcUtils.admin.billing.getWalletBalance.invalidate();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const columns = [
        columnHelper.accessor('id', {
            id: 'id',
            header: 'ID',
            sortable: 'id',
            cell: ({ value }) => <Text>{String(value)}</Text>,
        }),
        columnHelper.accessor('serviceCode', {
            id: 'serviceCode',
            header: 'Code',
            sortable: 'serviceCode',
            cell: ({ value }) => (
                <Tag variant="outlined" color="blue">
                    {value}
                </Tag>
            ),
        }),
        columnHelper.accessor('amount', {
            id: 'amount',
            header: 'Amount',
            sortable: 'amount',
            cell: ({ value }) => (
                <Text strong type={value.isNegative() ? 'danger' : 'success'}>
                    {value.isNegative() ? '-' : '+'}US${Math.abs(Number(value)).toFixed(2)}
                </Text>
            ),
        }),
        columnHelper.accessor('createdAt', {
            id: 'createdAt',
            header: 'Date',
            sortable: 'createdAt',
            cell: ({ value }) => <Text type="secondary">{DateTime.fromJSDate(value).toISO()}</Text>,
        }),
        columnHelper.accessor('note', {
            id: 'note',
            header: 'Note',
            cell: ({ value }) => <Text>{value || 'N/A'}</Text>,
        }),
    ] as const;

    const tableProps = usePrismaTable({
        data: transactionsData?.rows ?? [],
        total: transactionsData?.total ?? 0,
        loading: transactionsLoading,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: (params) => {
            setQueryParams((prev) => ({
                ...params,
                where: { ...prev.where },
            }));
        },
        pagination: {
            position: 'bottom',
        },
    });

    return (
        <PageController route={Route}>
            <Drawer
                title={`${org?.name} Wallet`}
                open={open}
                onClose={() => setOpen(false)}
                size={1200}
                afterOpenChange={async (open) => {
                    if (!open) {
                        navigate({
                            to: '/admin-console/organisation-management/organisations/$orgId',
                            params: { orgId },
                        });
                    }
                }}
            >
                <div className="space-y-6">
                    <ReadOnlyBanner module="orgs" />
                    <Card className="shadow-xs">
                        <div className="space-y-2">
                            <Text className="text-subtle-foreground">Current Balance</Text>
                            <div>
                                {walletLoading ? (
                                    <Spin />
                                ) : (
                                    <Text className="font-semibold text-3xl">
                                        US${walletBalance ? walletBalance.balance.toFixed(2) : '0.00'}
                                    </Text>
                                )}
                            </div>
                        </div>
                    </Card>

                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <Text strong className="text-lg">
                                Transaction History
                            </Text>
                            <div className="flex items-center gap-4">
                                <Text type="secondary">{transactionsData?.total ?? 0} transactions</Text>
                                {!isReadOnly && (
                                    <Button
                                        type="primary"
                                        icon={<Plus />}
                                        onClick={() => setIsModalOpen(true)}
                                        loading={addTransactionMutation.isPending}
                                    >
                                        Add Transaction
                                    </Button>
                                )}
                            </div>
                        </div>

                        <PrismaTable {...tableProps} className="shadow-xs" scroll={{ x: 500 }} />
                    </div>
                </div>

                <Modal
                    title="Add Transaction"
                    open={isModalOpen}
                    onCancel={() => {
                        setIsModalOpen(false);
                        form.resetFields();
                    }}
                    footer={null}
                    mask={{ closable: false }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={async (values: { type: 'credit' | 'debit'; amount: Decimal; note: string }) => {
                            try {
                                await addTransactionMutation.mutateAsync({
                                    orgId,
                                    amount: values.type === 'credit' ? values.amount : values.amount.neg(),
                                    note: `Transaction type: ${values.type} / ${values.note}`,
                                });
                            } catch (error) {
                                errorMessage.show(error);
                            }
                        }}
                        className="mt-4"
                    >
                        <Form.Item
                            name="type"
                            label="Transaction Type"
                            rules={[{ required: true, message: 'Please select a transaction type' }]}
                        >
                            <Select>
                                <Select.Option value="credit">Credit</Select.Option>
                                <Select.Option value="debit">Debit</Select.Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="amount"
                            label="Amount"
                            rules={buildCurrencyInputRules({ isRequired: true, gtZero: true })}
                        >
                            <CurrencyInput />
                        </Form.Item>

                        <Form.Item
                            name="note"
                            label="Note"
                            rules={[{ required: true, message: 'Please enter a note' }]}
                        >
                            <Input.TextArea placeholder="Enter a note for this transaction" />
                        </Form.Item>

                        <div className="flex justify-end gap-2">
                            <Button
                                type="secondary"
                                onClick={() => {
                                    setIsModalOpen(false);
                                    form.resetFields();
                                }}
                            >
                                Cancel
                            </Button>
                            <Button type="primary" htmlType="submit" loading={addTransactionMutation.isPending}>
                                Add Transaction
                            </Button>
                        </div>
                    </Form>
                </Modal>
            </Drawer>
        </PageController>
    );
}
